/**
 * ThemeToggle — tri-state cycle (Dark → Light → Auto → Dark).
 *
 * Shows the icon of the current mode. Auto shows a monitor icon plus a
 * small "AUTO" badge that surfaces the resolved theme colour so users
 * know at a glance whether the system currently reads dark or light.
 */
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

const LABELS = {
  dark: "Elite Performance (dark). Click to switch to Clean Professional (light).",
  light: "Clean Professional (light). Click to switch to Auto (follow system).",
  auto: "Auto — follows your system preference. Click to switch to Elite Performance (dark).",
};

export default function ThemeToggle({ compact = false }) {
  const { mode, theme, cycle } = useTheme();
  const label = LABELS[mode] || LABELS.dark;
  const Icon = mode === "dark" ? Sun : mode === "light" ? Moon : Monitor;
  const iconColor = mode === "dark" ? "var(--volt)" : mode === "light" ? "var(--azure)" : "var(--teal)";

  return (
    <button
      type="button"
      onClick={cycle}
      data-testid="theme-toggle"
      data-theme-mode={mode}
      data-theme-effective={theme}
      aria-label={label}
      title={label}
      className={
        "relative inline-flex items-center justify-center border transition-colors " +
        (compact ? "w-9 h-9" : "w-11 h-11") +
        " hover:brightness-110"
      }
      style={{
        background: "var(--surface)",
        borderColor: "var(--border)",
        color: "var(--text)",
        borderRadius: 999,
      }}
    >
      <Icon
        className={(compact ? "w-4 h-4" : "w-5 h-5") + " transition-transform duration-300"}
        style={{ color: iconColor }}
      />
      {mode === "auto" && (
        <span
          className="absolute -bottom-1 -right-1 px-1 rounded-sm font-mono font-bold"
          style={{
            fontSize: "8px",
            letterSpacing: "0.06em",
            background: "var(--teal)",
            color: "#0A0A0A",
            lineHeight: 1.3,
          }}
          data-testid="theme-toggle-auto-badge"
        >AUTO</span>
      )}
    </button>
  );
}
